import { Anchor, Box, Flex, Paper, Stack, Text } from '@mantine/core';
import dayjs from 'dayjs';
import { useProfile } from 'init/use-enforce-profile';
import { useEffect, useRef } from 'react';
import HTML from 'shared/components/HTML/HTML';
import type { InvoiceFeedback } from 'shared/hooks/use-load-third-party-case';

type MessageListProps = {
  messages: NonNullable<InvoiceFeedback>['conversation'];
};

export function MessageList({ messages }: MessageListProps) {
  const profile = useProfile();
  const messagesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messagesRef.current && messages.messages.length > 0) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <Box ref={messagesRef} mah={400} style={{ overflowY: 'auto' }} px="xs">
      <Stack gap="md" py="xs">
        {messages.messages.map((message, index) => {
          if (message.isSystem) {
            return (
              <Box key={message.id ?? index} ta="center" py={4}>
                <Text size="xs" tt="uppercase" c="dimmed" mb={4}>
                  {dayjs(message.date).format('LLL')}
                </Text>
                <Text size="sm" c="dimmed" fs="italic">
                  <HTML html={message.body} />
                </Text>
              </Box>
            );
          }

          const isRight = message.owner || message.author === profile.name.full;

          return (
            <Flex
              key={message.id ?? index}
              direction="column"
              align={isRight ? 'flex-end' : 'flex-start'}
              gap={4}
            >
              <Text size="xs" tt="uppercase" c="dimmed">
                {dayjs(message.date).format('LLL')}
              </Text>
              <Paper
                maw={400}
                p="md"
                radius="lg"
                bg={isRight ? 'purple.1' : 'white'}
                style={isRight ? undefined : { border: '1px solid var(--mantine-color-gray-2)' }}
              >
                <Stack gap={4}>
                  <Text size="sm" fw={700} c="purple.7">
                    {message.author}
                  </Text>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    <HTML html={message.body} />
                  </Text>
                  {message.attachmentUrl && (
                    <Anchor
                      href={message.attachmentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="xs"
                      fw={700}
                    >
                      {message.attachmentName}
                    </Anchor>
                  )}
                </Stack>
              </Paper>
            </Flex>
          );
        })}
      </Stack>
    </Box>
  );
}
